import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import { COLORS, SPACING, FONT_SIZE } from '../constants/theme';
import MetricDetailsModal from './MetricDetailsModal';

/**
 * PerformanceMetricsDashboard Component
 *
 * Displays player rankings across different stat categories
 * with multi-team selection for cross-team comparisons
 *
 * @param {Object} props
 * @param {Object} props.profile - Coach/admin profile with organization_id
 * @param {Array} props.teams - Available teams for coach
 */
const PerformanceMetricsDashboard = ({ profile, teams }) => {
  const { isDesktop } = useResponsive();

  const [statCategories, setStatCategories] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [rankings, setRankings] = useState({});
  const [loading, setLoading] = useState(true);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLatestOnly, setShowLatestOnly] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [playerIds, setPlayerIds] = useState([]);

  // Get unique organizations from teams
  const organizations = teams
    ? [
        ...new Map(
          teams
            .filter((t) => t.organization_id && t.organizations?.name)
            .map((t) => [t.organization_id, { id: t.organization_id, name: t.organizations.name }])
        ).values(),
      ]
    : [];

  // Initialize selected organization with first org or profile's org
  useEffect(() => {
    if (teams && teams.length > 0 && !selectedOrgId) {
      // Try profile org first, then first team's org
      const defaultOrgId = profile?.organization_id || teams[0]?.organization_id;
      if (defaultOrgId) {
        setSelectedOrgId(defaultOrgId);
      }
    }
  }, [teams, profile?.organization_id, selectedOrgId]);

  // Load stat categories when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      loadStatCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  // Load rankings when team selection or toggle changes
  useEffect(() => {
    if (statCategories.length > 0 && selectedTeamIds.length > 0) {
      loadRankings();
    }
  }, [selectedTeamIds, statCategories, showLatestOnly]);

  // Filter teams by selected organization
  const filteredTeams = selectedOrgId
    ? teams?.filter((t) => t.organization_id === selectedOrgId) || []
    : teams || [];

  // Initialize with all teams from selected org
  useEffect(() => {
    if (filteredTeams.length > 0) {
      setSelectedTeamIds(filteredTeams.map((t) => t.id));
    }
  }, [selectedOrgId]); // Reset when org changes

  const loadStatCategories = async () => {
    try {
      setLoading(true);
      setError('');

      if (!selectedOrgId) {
        setStatCategories([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('stat_categories')
        .select('*')
        .eq('organization_id', selectedOrgId)
        .order('display_order')
        .order('name');

      if (error) throw error;
      setStatCategories(data || []);
    } catch (err) {
      console.error('Error loading stat categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRankings = async () => {
    try {
      setRankingsLoading(true);
      setError('');

      // Get all player IDs from selected teams
      const { data: players, error: playersError } = await supabase
        .from('profiles')
        .select('id, team_id')
        .eq('role', 'player')
        .in('team_id', selectedTeamIds);

      if (playersError) throw playersError;

      const fetchedPlayerIds = players?.map((p) => p.id) || [];
      setPlayerIds(fetchedPlayerIds);

      if (fetchedPlayerIds.length === 0) {
        setRankings({});
        setRankingsLoading(false);
        return;
      }

      // Load rankings for all stat categories in parallel
      const rankingsPromises = statCategories.map(async (category) => {
        if (showLatestOnly) {
          // Fetch all stats for this category, then filter to latest per player
          const { data: allStats, error } = await supabase
            .from('player_stats')
            .select(
              `
              id,
              value,
              recorded_date,
              player_id,
              player:profiles!player_id(
                id,
                full_name,
                jersey_number,
                team:teams(id, name)
              )
            `
            )
            .eq('stat_category_id', category.id)
            .in('player_id', fetchedPlayerIds)
            .order('recorded_date', { ascending: false });

          if (error) {
            console.error(`Error loading rankings for ${category.name}:`, error);
            return { categoryId: category.id, data: [] };
          }

          // Group by player and keep only the latest stat per player
          const latestByPlayer = {};
          for (const stat of allStats || []) {
            if (!latestByPlayer[stat.player_id]) {
              latestByPlayer[stat.player_id] = stat;
            }
          }

          // Convert to array and sort by value
          const latestStats = Object.values(latestByPlayer);
          latestStats.sort((a, b) => {
            if (category.sort_direction === 'asc') {
              return a.value - b.value;
            } else {
              return b.value - a.value;
            }
          });

          return { categoryId: category.id, data: latestStats.slice(0, 10) };
        } else {
          // Show all stats (players can appear multiple times)
          const { data, error } = await supabase
            .from('player_stats')
            .select(
              `
              id,
              value,
              recorded_date,
              player:profiles!player_id(
                id,
                full_name,
                jersey_number,
                team:teams(id, name)
              )
            `
            )
            .eq('stat_category_id', category.id)
            .in('player_id', fetchedPlayerIds)
            .order('value', { ascending: category.sort_direction === 'asc' })
            .limit(10);

          if (error) {
            console.error(`Error loading rankings for ${category.name}:`, error);
            return { categoryId: category.id, data: [] };
          }

          return { categoryId: category.id, data: data || [] };
        }
      });

      // Wait for all rankings to load
      const results = await Promise.all(rankingsPromises);

      // Build rankings object from results
      const rankingsData = {};
      results.forEach((result) => {
        rankingsData[result.categoryId] = result.data;
      });

      setRankings(rankingsData);
    } catch (err) {
      console.error('Error loading rankings:', err);
      setError(err.message);
    } finally {
      setRankingsLoading(false);
    }
  };

  const handleTeamToggle = (teamId) => {
    setSelectedTeamIds((prev) => {
      if (prev.includes(teamId)) {
        // Don't allow deselecting all teams
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedTeamIds(filteredTeams.map((t) => t.id));
  };

  const handleDeselectAll = () => {
    // Keep at least one team selected
    if (filteredTeams.length > 0) {
      setSelectedTeamIds([filteredTeams[0].id]);
    }
  };

  const handleOrgChange = (orgId) => {
    setSelectedOrgId(orgId);
    // Reset team selection and rankings when org changes
    setSelectedTeamIds([]);
    setRankings({});
  };

  const getRankColor = (index) => {
    if (index === 0) return '#FFD700'; // Gold
    if (index === 1) return '#C0C0C0'; // Silver
    if (index === 2) return '#CD7F32'; // Bronze
    return 'transparent';
  };

  const styles = {
    container: {
      padding: isDesktop ? SPACING.xl : SPACING.md,
    },
    header: {
      marginBottom: SPACING.xl,
    },
    title: {
      fontSize: isDesktop ? FONT_SIZE['2xl'] : FONT_SIZE.xl,
      fontWeight: '600',
      color: COLORS.gray[900],
      marginBottom: SPACING.md,
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.md,
      padding: SPACING.md,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: SPACING.lg,
    },
    toggleLabel: {
      fontSize: FONT_SIZE.base,
      fontWeight: '500',
      color: COLORS.gray[700],
    },
    toggleButtons: {
      display: 'flex',
      backgroundColor: COLORS.gray[100],
      borderRadius: '6px',
      padding: '4px',
    },
    toggleButton: {
      padding: '8px 16px',
      fontSize: FONT_SIZE.sm,
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: 'transparent',
      color: COLORS.gray[600],
    },
    toggleButtonActive: {
      backgroundColor: 'white',
      color: COLORS.primary,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    orgSelector: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '20px' : '16px',
      marginBottom: SPACING.lg,
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      alignItems: isDesktop ? 'center' : 'flex-start',
      gap: isDesktop ? SPACING.lg : SPACING.sm,
    },
    orgSelectorLabel: {
      fontSize: FONT_SIZE.base,
      fontWeight: '600',
      color: COLORS.gray[900],
      minWidth: '120px',
    },
    orgDropdown: {
      flex: isDesktop ? 1 : 'auto',
      width: isDesktop ? 'auto' : '100%',
      maxWidth: '400px',
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: 'white',
      cursor: 'pointer',
    },
    teamSelector: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '20px' : '16px',
      marginBottom: SPACING.xl,
    },
    teamSelectorTitle: {
      fontSize: FONT_SIZE.lg,
      fontWeight: '600',
      color: COLORS.gray[900],
      marginBottom: SPACING.md,
    },
    teamCheckboxes: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: SPACING.md,
      marginBottom: SPACING.md,
    },
    teamCheckbox: {
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.sm,
      padding: SPACING.sm,
      borderRadius: '4px',
      cursor: 'pointer',
      userSelect: 'none',
    },
    teamCheckboxInput: {
      width: '18px',
      height: '18px',
      cursor: 'pointer',
    },
    teamCheckboxLabel: {
      fontSize: FONT_SIZE.base,
      color: COLORS.gray[700],
      cursor: 'pointer',
    },
    selectButtons: {
      display: 'flex',
      gap: SPACING.sm,
    },
    selectButton: {
      padding: '6px 12px',
      fontSize: FONT_SIZE.sm,
      fontWeight: '500',
      border: `1px solid ${COLORS.gray[300]}`,
      borderRadius: '4px',
      backgroundColor: 'white',
      color: COLORS.gray[700],
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    metricsGrid: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : '1fr',
      gap: SPACING.lg,
    },
    metricCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '20px' : '16px',
      display: 'flex',
      flexDirection: 'column',
    },
    metricHeader: {
      marginBottom: SPACING.md,
      paddingBottom: SPACING.sm,
      borderBottom: `2px solid ${COLORS.gray[200]}`,
    },
    metricName: {
      fontSize: FONT_SIZE.lg,
      fontWeight: '600',
      color: COLORS.gray[900],
      marginBottom: '4px',
    },
    metricUnit: {
      fontSize: FONT_SIZE.sm,
      color: COLORS.gray[500],
    },
    sortDirection: {
      fontSize: FONT_SIZE.xs,
      color: COLORS.gray[500],
      fontStyle: 'italic',
    },
    rankingsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
      flex: 1,
    },
    rankingItem: {
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.sm,
      padding: SPACING.sm,
      borderRadius: '4px',
      transition: 'background-color 0.2s',
    },
    rank: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: FONT_SIZE.sm,
      fontWeight: '600',
      flexShrink: 0,
    },
    playerInfo: {
      flex: 1,
      minWidth: 0,
    },
    playerName: {
      fontSize: FONT_SIZE.sm,
      fontWeight: '500',
      color: COLORS.gray[900],
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    teamName: {
      fontSize: FONT_SIZE.xs,
      color: COLORS.gray[500],
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statValue: {
      fontSize: FONT_SIZE.base,
      fontWeight: '600',
      color: COLORS.gray[900],
      flexShrink: 0,
    },
    statMeta: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '2px',
      flexShrink: 0,
    },
    recordedDate: {
      fontSize: FONT_SIZE.xs,
      color: COLORS.gray[500],
      fontStyle: 'italic',
    },
    emptyState: {
      textAlign: 'center',
      padding: SPACING.xl,
      color: COLORS.gray[500],
    },
    error: {
      padding: SPACING.md,
      backgroundColor: COLORS.alert.error.bg,
      color: COLORS.alert.error.text,
      border: `1px solid ${COLORS.alert.error.border}`,
      borderRadius: '4px',
      marginBottom: SPACING.md,
    },
    loadingOverlay: {
      position: 'relative',
      opacity: rankingsLoading ? 0.5 : 1,
      pointerEvents: rankingsLoading ? 'none' : 'auto',
      transition: 'opacity 0.2s',
    },
    loadingIndicator: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      padding: SPACING.lg,
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.md,
      zIndex: 1000,
    },
    viewAllButton: {
      width: '100%',
      padding: SPACING.sm,
      marginTop: SPACING.sm,
      fontSize: FONT_SIZE.sm,
      fontWeight: '500',
      color: COLORS.primary,
      backgroundColor: 'white',
      border: `1px solid ${COLORS.primary}`,
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>Loading performance metrics...</div>
      </div>
    );
  }

  if (statCategories.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p>No stat categories available.</p>
          <p>Visit the "Stat Categories" tab to create performance metrics.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.header}>
        <h1 style={styles.title}>Performance Metrics</h1>
      </div>

      {/* Organization Selection */}
      {organizations.length > 1 && (
        <div style={styles.orgSelector}>
          <label style={styles.orgSelectorLabel}>Organization:</label>
          <select
            style={styles.orgDropdown}
            value={selectedOrgId}
            onChange={(e) => handleOrgChange(e.target.value)}
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* View Mode Toggle */}
      <div style={styles.toggleContainer}>
        <span style={styles.toggleLabel}>Show:</span>
        <div style={styles.toggleButtons}>
          <button
            style={{
              ...styles.toggleButton,
              ...(showLatestOnly ? styles.toggleButtonActive : {}),
            }}
            onClick={() => setShowLatestOnly(true)}
          >
            Latest Entry Per Player
          </button>
          <button
            style={{
              ...styles.toggleButton,
              ...(!showLatestOnly ? styles.toggleButtonActive : {}),
            }}
            onClick={() => setShowLatestOnly(false)}
          >
            All Stats Recorded
          </button>
        </div>
      </div>

      {/* Team Selection */}
      <div style={styles.teamSelector}>
        <h2 style={styles.teamSelectorTitle}>Select Teams</h2>
        <div style={styles.teamCheckboxes}>
          {filteredTeams.map((team) => (
            <label key={team.id} style={styles.teamCheckbox}>
              <input
                type="checkbox"
                style={styles.teamCheckboxInput}
                checked={selectedTeamIds.includes(team.id)}
                onChange={() => handleTeamToggle(team.id)}
              />
              <span style={styles.teamCheckboxLabel}>{team.name}</span>
            </label>
          ))}
        </div>
        <div style={styles.selectButtons}>
          <button style={styles.selectButton} onClick={handleSelectAll}>
            Select All
          </button>
          <button style={styles.selectButton} onClick={handleDeselectAll}>
            Deselect All
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {rankingsLoading && (
        <div style={styles.loadingIndicator}>
          <div style={{ fontSize: FONT_SIZE.base, fontWeight: '500' }}>Loading rankings...</div>
        </div>
      )}

      {/* Metrics Grid */}
      <div style={{ ...styles.metricsGrid, ...styles.loadingOverlay }}>
        {statCategories.map((category) => {
          const categoryRankings = rankings[category.id] || [];

          return (
            <div key={category.id} style={styles.metricCard}>
              <div style={styles.metricHeader}>
                <div style={styles.metricName}>{category.name}</div>
                <div style={styles.metricUnit}>Unit: {category.unit}</div>
                <div style={styles.sortDirection}>
                  {category.sort_direction === 'desc' ? '↑ Higher is Better' : '↓ Lower is Better'}
                </div>
              </div>

              <div style={styles.rankingsList}>
                {categoryRankings.length === 0 ? (
                  <div style={styles.emptyState}>No stats recorded</div>
                ) : (
                  <>
                    {categoryRankings.map((stat, index) => (
                      <div key={stat.id} style={styles.rankingItem}>
                        <div
                          style={{
                            ...styles.rank,
                            backgroundColor: getRankColor(index),
                            color: index < 3 ? 'white' : COLORS.gray[700],
                            border: index < 3 ? 'none' : `2px solid ${COLORS.gray[300]}`,
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={styles.playerInfo}>
                          <div style={styles.playerName}>
                            {stat.player?.jersey_number && `#${stat.player.jersey_number} `}
                            {stat.player?.full_name || 'Unknown Player'}
                          </div>
                          {selectedTeamIds.length > 1 && stat.player?.team && (
                            <div style={styles.teamName}>{stat.player.team.name}</div>
                          )}
                        </div>
                        <div style={styles.statMeta}>
                          <div style={styles.statValue}>
                            {stat.value} {category.unit}
                          </div>
                          {!showLatestOnly && stat.recorded_date && (
                            <div style={styles.recordedDate}>
                              {new Date(stat.recorded_date).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      style={styles.viewAllButton}
                      onClick={() => setSelectedCategory(category)}
                    >
                      View All
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Metric Details Modal */}
      {selectedCategory && (
        <MetricDetailsModal
          category={selectedCategory}
          playerIds={playerIds}
          showLatestOnly={showLatestOnly}
          onClose={() => setSelectedCategory(null)}
        />
      )}
    </div>
  );
};

export default PerformanceMetricsDashboard;
